// XTime: wheel time picker (hour 1-12, minutes in 5-minute steps, AM/PM). Drag, mouse wheel, tap a row, arrow keys
// (Home/End too). Calls onPick("8:30 AM") on every change. 220px tall; the selected row sits in the middle band.
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { rgba } from '../theme';
import { haptic } from '../native';

export interface XTimeProps {
  /** '9:00 AM' */
  value: string;
  onPick?: (value: string) => void;
  /** Accent for the selection band (default var(--acc)). */
  acc?: string;
  /** Background the fades blend into (hex). Default #0A0908. */
  bg?: string;
  /** Width in px (default 300, max 100% of the parent). */
  w?: number;
  class?: string;
  style?: JSX.CSSProperties;
}

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const APS = ['AM', 'PM'] as const;
const ROW = 44;
const pad = (n: number) => (n < 10 ? '0' : '') + n;

interface Sel { hi: number; mi: number; ai: number }
function parse(v: string): Sel {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(v || '').trim());
  if (!m) return { hi: 8, mi: 0, ai: 0 };
  const hi = HOURS.indexOf(Number(m[1]));
  return { hi: hi >= 0 ? hi : 8, mi: Math.round(Number(m[2]) / 5) % 12, ai: m[3].toUpperCase() === 'PM' ? 1 : 0 };
}
const fmt = (x: Sel) => `${HOURS[x.hi]}:${pad(MINS[x.mi])} ${APS[x.ai]}`;

const COLS: { key: keyof Sel; label: string; list: string[]; w: number }[] = [
  { key: 'hi', label: 'Hour', list: HOURS.map(String), w: 72 },
  { key: 'mi', label: 'Minutes', list: MINS.map(pad), w: 72 },
  { key: 'ai', label: 'AM or PM', list: [...APS], w: 66 },
];

export function XTime({ value, onPick, acc = 'var(--acc)', bg = '#0A0908', w = 300, class: cls, style }: XTimeProps) {
  const [sel, setSel] = useState<Sel>(() => parse(value));
  const [dragging, setDragging] = useState<keyof Sel | null>(null);
  const cur = useRef(sel);
  cur.current = sel;
  const drag = useRef<{ key: keyof Sel; y: number; idx: number; moved: boolean; id: number } | null>(null);
  const lastWheel = useRef(0);

  // Follow outside changes of `value` (not our own echoes).
  useEffect(() => { if (fmt(cur.current) !== value) setSel(parse(value)); }, [value]);

  const commit = (patch: Partial<Sel>) => {
    const n = { ...cur.current, ...patch };
    if (n.hi === cur.current.hi && n.mi === cur.current.mi && n.ai === cur.current.ai) return;
    cur.current = n;
    setSel(n);
    haptic('selection');
    onPick?.(fmt(n));
  };
  const clamp = (key: keyof Sel, n: number) => Math.max(0, Math.min(COLS.find((c) => c.key === key)!.list.length - 1, n));
  const clear = /^#[0-9a-f]{6}$/i.test(bg) ? rgba(bg, 0) : 'rgba(0,0,0,0)';

  return (
    <div class={cls} style={{ width: w, maxWidth: '100%', height: 220, position: 'relative', background: bg, fontFamily: 'var(--xm-font)', userSelect: 'none', WebkitUserSelect: 'none', flex: 'none', ...style }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 88, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.08)', border: `1px solid ${acc}` }} />
      <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', gap: 4 }}>
        {COLS.map((col) => {
          const idx = sel[col.key];
          const max = col.list.length - 1;
          return (
            <div
              key={col.key}
              role="spinbutton" tabIndex={0} aria-label={col.label} aria-valuetext={col.list[idx]} aria-valuenow={idx} aria-valuemin={0} aria-valuemax={max}
              style={{ position: 'relative', width: col.w, height: 220, overflow: 'hidden', cursor: dragging === col.key ? 'grabbing' : 'grab', touchAction: 'none', outlineOffset: -2, borderRadius: 14 }}
              onPointerDown={(e) => {
                drag.current = { key: col.key, y: e.clientY, idx: cur.current[col.key], moved: false, id: e.pointerId };
                try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
                setDragging(col.key);
              }}
              onPointerMove={(e) => {
                const g = drag.current;
                if (!g || g.key !== col.key || g.id !== e.pointerId) return;
                if (Math.abs(g.y - e.clientY) > 5) g.moved = true;
                commit({ [col.key]: clamp(col.key, g.idx + Math.round((g.y - e.clientY) / ROW)) });
              }}
              onPointerUp={(e) => {
                const g = drag.current;
                drag.current = null;
                setDragging(null);
                if (g && g.key === col.key && !g.moved) {
                  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  commit({ [col.key]: clamp(col.key, cur.current[col.key] + Math.round((e.clientY - (r.top + 110)) / ROW)) });
                }
              }}
              onPointerCancel={() => { drag.current = null; setDragging(null); }}
              onWheel={(e) => {
                e.preventDefault();
                const now = Date.now();
                if (now - lastWheel.current < 90) return;
                lastWheel.current = now;
                commit({ [col.key]: clamp(col.key, cur.current[col.key] + (e.deltaY > 0 ? 1 : -1)) });
              }}
              onKeyDown={(e) => {
                const k = e.key;
                let n: number | null = null;
                if (k === 'ArrowDown') n = cur.current[col.key] + 1;
                else if (k === 'ArrowUp') n = cur.current[col.key] - 1;
                else if (k === 'Home') n = 0;
                else if (k === 'End') n = max;
                else if (k === 'PageDown') n = cur.current[col.key] + 3;
                else if (k === 'PageUp') n = cur.current[col.key] - 3;
                if (n == null) return;
                e.preventDefault();
                commit({ [col.key]: clamp(col.key, n) });
              }}
            >
              <div style={{ position: 'absolute', left: 0, right: 0, top: 0, transform: `translateY(${(2 - idx) * ROW}px)`, transition: dragging === col.key ? 'transform 90ms linear' : 'transform 200ms cubic-bezier(.2,.9,.3,1)' }}>
                {col.list.map((t, i) => {
                  const d = Math.abs(i - idx);
                  return (
                    <div key={t} aria-hidden="true" style={{
                      height: ROW, display: 'flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums',
                      fontSize: d === 0 ? 26 : d === 1 ? 21 : 18, fontWeight: d === 0 ? 800 : d === 1 ? 600 : 500,
                      color: d === 0 ? '#F5F1EA' : d === 1 ? '#B9B2A6' : '#77716A',
                    }}>{t}</div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <span aria-hidden="true" style={{ position: 'absolute', top: 88, left: '50%', marginLeft: -40, width: 10, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800, color: '#F5F1EA', pointerEvents: 'none' }}>:</span>
      <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 80, background: `linear-gradient(180deg, ${bg} 0%, ${clear} 100%)`, pointerEvents: 'none' }} />
      <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 80, background: `linear-gradient(0deg, ${bg} 0%, ${clear} 100%)`, pointerEvents: 'none' }} />
    </div>
  );
}
